import React, { useEffect, useState } from "react";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import closeIcon from "../../assets/icons/close-icon.svg";
import CreatedTimeFilter from "../leads/lead-filter/lead-filter-options/created-time-filter";
import dayjs, { Dayjs } from "dayjs";
import UserTypeFilter from "./user-type-filter";
import { LeadFilterOptionsProps } from "../leads/lead-filter/lead-filter.props";

const UnsubscribeUsersFilterOptions: React.FC<LeadFilterOptionsProps> = (
  props: any
) => {
  const {
    setSelectedLeadsFilters,
    setIsOpenFilter,
    isOpenFilter,
    selectedLeadsFilterOptions,
    setSelectedLeadsFilterOptions,
    onApplyFilters,
    activeFilters,
  } = props;
  const [createdTime, setCreatedTime] = useState<string[]>([]);
  const [createdTimeError, setCreatedTimeError] = useState("");
  const [createdTimeStart, setCreatedTimeStart] = useState<Dayjs | null>(null);
  const [createdTimeEnd, setCreatedTimeEnd] = useState<Dayjs | null>(null);
  const [createdTimeStartError, setCreatedTimeStartError] = useState("");
  const [createdTimeEndError, setCreatedTimeEndError] = useState("");
  const [userType, setUserType] = useState<string[]>([]);
  const [userTypeError, setUserTypeError] = useState("");
  const [draftSelectedLeadsFilterOptions, setDraftSelectedLeadsFilterOptions] =
    useState<any[]>([]);

  useEffect(() => {
    if (isOpenFilter) {
      // Step 1: Pre-check fields based on activeFilters
      const updatedDraft = selectedLeadsFilterOptions.map((option: any) => {
        switch (option.labels) {
          case "Opt Out Date":
            return {
              ...option,
              value: !!(
                activeFilters?.createdTimeStart && activeFilters?.createdTimeEnd
              ),
            };
          case "User Type":
            return {
              ...option,
              value: !!activeFilters?.userType?.length,
            };
          default:
            return option;
        }
      });

      setDraftSelectedLeadsFilterOptions(updatedDraft);

      if (activeFilters?.createdTimeStart && activeFilters?.createdTimeEnd) {
        setCreatedTime(["Custom"]);
        setCreatedTimeStart(dayjs(activeFilters.createdTimeStart));
        setCreatedTimeEnd(dayjs(activeFilters.createdTimeEnd));
      }

      if (activeFilters?.userType?.length) {
        setUserType(activeFilters.userType);
      }
    }
  }, [isOpenFilter]);

  const isAnyFilterSelected = draftSelectedLeadsFilterOptions.some(
    (option: any) => option.value
  );
  const handleChange = (e: any) => {
    const updatedDraft = draftSelectedLeadsFilterOptions.map((data: any) =>
      data?.labels === e.target.id ? { ...data, value: !data?.value } : data
    );

    const isCreatedTimeChecked =
      e.target.id === "Opt Out Date" && e.target.checked;
    const isCreatedTimeUnchecked =
      e.target.id === "Opt Out Date" && !e.target.checked;
    if (isCreatedTimeChecked) {
      setCreatedTime(["Custom"]);
    }

    if (isCreatedTimeUnchecked) {
      setCreatedTime([]);
      setCreatedTimeStart(null);
      setCreatedTimeEnd(null);
      setCreatedTimeError("");
      setCreatedTimeStartError("");
      setCreatedTimeEndError("");
    }
    const isUserTypeChecked = e.target.id === "User Type" && e.target.checked;
    const isUserTypeUnchecked =
      e.target.id === "User Type" && !e.target.checked;

    if (isUserTypeUnchecked) {
      setUserType([]);
      setUserTypeError("");
    }
    setDraftSelectedLeadsFilterOptions(updatedDraft);
  };

  const handleReset = () => {
    const resetFilters = selectedLeadsFilterOptions.map((option: any) => ({
      ...option,
      value: false,
    }));

    setSelectedLeadsFilterOptions(resetFilters);
    setDraftSelectedLeadsFilterOptions(resetFilters);

    setCreatedTimeStart(null);
    setCreatedTimeEnd(null);
    setCreatedTimeStartError("");
    setCreatedTimeEndError("");
    setCreatedTime([]);
    setCreatedTimeError("");
    setUserType([]);
    setUserTypeError("");
    setSelectedLeadsFilters("");

    props.onApplyFilters({});
    setSelectedLeadsFilters("");
  };

  const handleCreatedTimeDateRange = (from: Date | null, to: Date | null) => {
    setCreatedTimeStart(from ? dayjs(from) : null);
    setCreatedTimeEnd(to ? dayjs(to) : null);
    if (from) setCreatedTimeStartError("");
    if (to) setCreatedTimeEndError("");
  };

  const isCreatedTimeEnabled = draftSelectedLeadsFilterOptions.find(
    (opt: any) => opt.labels === "Opt Out Date"
  )?.value;
  const isUserTypeEnabled = draftSelectedLeadsFilterOptions.find(
    (opt: any) => opt.labels === "User Type"
  )?.value;

  const renderFilterField = (optionLabel: string) => {
    switch (optionLabel) {
      case "Opt Out Date":
        return (
          <CreatedTimeFilter
            value={"Custom"}
            onChange={() => {}}
            isCreatedTimeEnabled={isCreatedTimeEnabled}
            onDateRangeChange={handleCreatedTimeDateRange}
            createdTimeError={createdTimeError}
            createdTimeStartError={createdTimeStartError}
            createdTimeEndError={createdTimeEndError}
            setCreatedTimeFromDate={setCreatedTimeStart}
            setCretedTimeToDate={setCreatedTimeEnd}
            showDropdown={false}
            fromDate={createdTimeStart}
            toDate={createdTimeEnd}
          />
        );
      case "User Type":
        return (
          <UserTypeFilter
            value={userType}
            onChange={setUserType}
            isUserTypeEnabled={
              draftSelectedLeadsFilterOptions.find(
                (opt: any) => opt.labels === "User Type"
              )?.value
            }
            error={userTypeError}
          />
        );
      default:
        return null;
    }
  };

  const handleApplyFilters = () => {
    let hasError = false;

    const isCreatedTimeEnabled = draftSelectedLeadsFilterOptions.find(
      (opt: any) => opt.labels === "Opt Out Date"
    )?.value;

    const isUserTypeEnabled = draftSelectedLeadsFilterOptions.find(
      (opt: any) => opt.labels === "User Type"
    )?.value;

    const filterData: any = {};

    // ---------- Opt Out Date ----------
    if (isCreatedTimeEnabled) {
      if (createdTime.length === 0) {
        setCreatedTimeError("Select Opt Out Date");
        hasError = true;
      } else {
        setCreatedTimeError("");
      }

      if (createdTime.includes("Custom")) {
        if (!createdTimeStart) {
          setCreatedTimeStartError("Start date is required");
          hasError = true;
        } else if (
          !dayjs(createdTimeStart).isValid() ||
          dayjs(createdTimeStart).year() < 1900
        ) {
          setCreatedTimeStartError("Invalid date");
          hasError = true;
        } else {
          setCreatedTimeStartError("");
        }

        if (!createdTimeEnd) {
          setCreatedTimeEndError("End date is required");
          hasError = true;
        } else if (
          !dayjs(createdTimeEnd).isValid() ||
          dayjs(createdTimeEnd).year() < 1900
        ) {
          setCreatedTimeEndError("Invalid date");
          hasError = true;
        } else {
          setCreatedTimeEndError("");
        }

        if (
          createdTimeStart &&
          createdTimeEnd &&
          dayjs(createdTimeStart).isValid() &&
          dayjs(createdTimeEnd).isValid() &&
          dayjs(createdTimeStart).isAfter(dayjs(createdTimeEnd))
        ) {
          setCreatedTimeStartError(
            "Start date cannot be greater than end date"
          );
          setCreatedTimeEndError("End date cannot be less than start date");
          hasError = true;
        }

        if (!hasError) {
          filterData.createdTimeStart = createdTimeStart;
          filterData.createdTimeEnd = createdTimeEnd;
        }
      }
    }
    if (isUserTypeEnabled) {
      if (userType.length === 0) {
        setUserTypeError("User Type cannot be empty");
        hasError = true;
      } else {
        setUserTypeError("");
        filterData.userType = userType;
      }
    }

    // ---------- EXIT IF ERRORS ----------
    if (hasError) return;

    // ---------- APPLY FILTERS ----------
    onApplyFilters(filterData);
    setSelectedLeadsFilterOptions(draftSelectedLeadsFilterOptions);
    setSelectedLeadsFilters(
      draftSelectedLeadsFilterOptions.filter((data: any) => data.value)
    );
    setIsOpenFilter(false); // Close the popup
  };

  return (
    <div className="lead-filter-options-wrapper">
      <div className="lead-filter-options-container">
        <div className="header-wrapper">
          <div className="lead-filter-options-container-header">
            <div className="lead-filter-options-container-header-text">
              Filters
            </div>
            <div className="lead-filter-options-container-header-icon">
              <img
                src={closeIcon}
                alt="Filter Icon"
                onClick={() => {
                  setIsOpenFilter(false);
                }}
              />
            </div>
          </div>
          <div className="lead-filter-options-container-subtext">
            Filter By Fields
          </div>
        </div>
        <div className="lead-filter-options-container-list">
          {draftSelectedLeadsFilterOptions.map((option: any) => (
            <div>
              <label
                htmlFor={option?.labels}
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  ...(option?.value && { height: "10px", marginTop: "30px" }),
                }}
              >
                <Checkbox
                  checked={option?.value}
                  onChange={handleChange}
                  id={option?.labels}
                />
                <ListItemText primary={option?.labels} />
              </label>

              {option?.value && renderFilterField(option?.labels)}
            </div>
          ))}
          {isAnyFilterSelected && (
            <div className="lead-filter-buttons">
              <button
                className="cancel-button"
                onClick={() => {
                  handleReset();
                }}
              >
                Reset
              </button>
              <button
                className="next-button-wrapper"
                type="submit"
                onClick={handleApplyFilters}
              >
                Apply
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UnsubscribeUsersFilterOptions;